/**
 * SetupChecklist (kind-alignment §4) — the kind-specific setup guide that
 * follows the FirstRunTour. Owners only; mounts once the tour is dismissed
 * and the workspace isn't fully set up yet.
 *
 * Collapsed: a bottom-right pill (`Setup 4/6`) with a progress ring.
 * Expanded: a panel over a dismissible scrim; taking a step navigates and
 * re-collapses. Data-gated steps tick themselves off — from the
 * connected-integrations list, from the workspace facts GET /api/setup/progress
 * reads (V38), or when the merchant reaches the page the step is about (a
 * flow's canvas). Visits and "go and look" steps persist per kind on the user
 * profile (server-side, beside the tour flag). While mounted, the body
 * carries `setup-dock` so the app scroll container reserves bottom padding.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronRight, X, ListChecks } from 'lucide-react';
import api from '../../services/api';
import { apiClient } from '../../services/apiClient';
import { t } from '../../i18n';
import { useBusinessType, BUSINESS_TYPE_LABELS } from '../../config/businessPresets';
import { useConnectedIntegrations } from '../../hooks/useConnectedIntegrations';
import { resolveSteps, type ResolvedStep } from './steps';
import { ComingSoonBadge } from '../ui/ComingSoonBadge';
import { ProgressRing } from './ProgressRing';

/** Fired by FirstRunTour when the owner finishes/skips it in this session. */
export const TOUR_DISMISSED_EVENT = 'buzzbit:tour-dismissed';

/** Per-kind manually-taken step ids, mirroring User.setupChecklistDone. */
type DoneMap = Record<string, string[]>;

interface ChecklistBodyProps {
  setManualDone: React.Dispatch<React.SetStateAction<DoneMap>>;
  manualDone: DoneMap;
}

export function SetupChecklist(): React.ReactElement | null {
  const [eligible, setEligible] = useState(false);
  const [manualDone, setManualDone] = useState<DoneMap>({});

  useEffect(() => {
    let cancelled = false;
    let ownerConfirmed = false;
    let dismissedSeen = false;
    // Registered BEFORE the profile fetch resolves: if the owner dismisses the
    // tour while auth/profile are still loading, the event must not be lost.
    // Eligibility still waits for the role check below.
    const onDismiss = (): void => {
      dismissedSeen = true;
      if (ownerConfirmed && !cancelled) setEligible(true);
    };
    window.addEventListener(TOUR_DISMISSED_EVENT, onDismiss);
    (async () => {
      try {
        const [me, profile] = await Promise.all([api.auth.me(), api.userProfile.get()]);
        if (cancelled) return;
        const workspaceId = api.getWorkspaceId();
        const role = me.data?.workspaces?.find((w) => w.id === workspaceId)?.role;
        const dismissed = Boolean(profile.data?.onboardingTourDismissedAt) || dismissedSeen;
        const done = profile.data?.setupChecklistDone;
        if (done && typeof done === 'object') setManualDone(done);
        // Owners only, and only AFTER the tour — the checklist is its sequel.
        ownerConfirmed = role === 'OWNER';
        if (ownerConfirmed && dismissed) setEligible(true);
      } catch {
        // Setup guidance is never worth an error state.
      }
    })();
    return () => {
      cancelled = true;
      window.removeEventListener(TOUR_DISMISSED_EVENT, onDismiss);
    };
  }, []);

  if (!eligible) return null;
  return <ChecklistBody manualDone={manualDone} setManualDone={setManualDone} />;
}

function ChecklistBody({ manualDone, setManualDone }: ChecklistBodyProps): React.ReactElement | null {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessType, status: kindStatus, retry: retryKind } = useBusinessType();
  // `includeAdAccounts` because the ADS step claims an ad account is linked,
  // and that claim cannot be answered from /integrations at all — an ad grant
  // is an AdCredential, written only when the OAuth grant carried `ads_read`.
  // The step used to tick on a Facebook PAGE connection via the registry map,
  // so it read done while AdCredential had zero rows platform-wide. The
  // checklist is the only surface that makes this claim, so it is the only one
  // that pays for the extra request.
  //
  // `includeVerification` because a step must tick on a connection that WORKS:
  // the capability matrix demotes a platform whose stored authentication check
  // failed, so a dead token unticks its step instead of reading as done.
  const { connectedIds, loading, refresh: refreshConnections } = useConnectedIntegrations({ includeAdAccounts: true, includeVerification: true });
  const [expanded, setExpanded] = useState(false);
  // V38 — what the workspace's data says is done (verified domain, funnel on,
  // brand named, …). A failed read is "nothing proven yet", never an error.
  const [facts, setFacts] = useState<ReadonlySet<string>>(new Set());
  const refreshFacts = useCallback(async (): Promise<void> => {
    try {
      const res = await apiClient.get<{ data?: { facts?: unknown } }>('/setup/progress');
      const list = Array.isArray(res.data?.facts) ? res.data.facts.filter((f): f is string => typeof f === 'string') : [];
      setFacts((prev) => (list.length === prev.size && list.every((f) => prev.has(f)) ? prev : new Set(list)));
    } catch {
      // Same policy as the connection probes: no answer takes nothing away.
    }
  }, []);
  useEffect(() => {
    void refreshFacts();
  }, [refreshFacts]);
  // Held in a ref so `refresh` stays one function for the component's life —
  // the effects below key on it, and a new identity per render would re-read
  // on every render.
  const refreshConnectionsRef = useRef(refreshConnections);
  refreshConnectionsRef.current = refreshConnections;
  const refresh = useCallback(async (): Promise<void> => {
    await Promise.all([refreshConnectionsRef.current(), refreshFacts()]);
  }, [refreshFacts]);

  // §4: connection-gated steps tick themselves off. Modal-based connects
  // (Shopify, Claude key) don't reload the page, so re-derive the connected
  // set after every in-app navigation and whenever the window regains focus
  // (OAuth flows that finished in another tab).
  const firstRoute = useRef(true);
  useEffect(() => {
    if (firstRoute.current) {
      firstRoute.current = false;
      return;
    }
    void refresh();
  }, [location.pathname, refresh]);
  useEffect(() => {
    const onFocus = (): void => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const steps = resolveSteps(businessType, manualDone[businessType] ?? [], connectedIds, facts);
  const doneCount = steps.filter((s) => s.complete).length;
  const allDone = doneCount === steps.length;
  const active = !loading && !allDone;

  // Reserve bottom padding on the app scroll container while the pill floats.
  useEffect(() => {
    document.body.classList.toggle('setup-dock', active);
    return () => document.body.classList.remove('setup-dock');
  }, [active]);

  // V38 — "Look inside a flow" is done when a flow is actually opened, not
  // when its checklist row is clicked. Same kind gate as `takeStep` below: a
  // per-kind write is only honest once the kind is known.
  const visitedStep = steps.find((s) => s.visited !== undefined && !s.complete && s.visited.test(location.pathname));
  const visitedId = visitedStep?.id;
  useEffect(() => {
    if (visitedId === undefined || kindStatus !== 'ready') return;
    setManualDone((m) => ({ ...m, [businessType]: [...(m[businessType] ?? []), visitedId] }));
    void api.userProfile
      .update({ setupStepDone: { kind: businessType, stepId: visitedId } })
      .catch(() => undefined);
  }, [visitedId, kindStatus, businessType, setManualDone]);

  if (!active) return null;

  const takeStep = (step: ResolvedStep): void => {
    // WHY THE GATE, AND WHY IT IS NOT A QUEUE (task 98).
    //
    // `setupStepDone` is keyed by kind, and `businessType` is only a
    // presentation fallback until `kindStatus` is 'ready'. Writing before then
    // files a services merchant's progress under `kind: 'ecommerce'`, where
    // their real checklist never sees it — a failed read turning into bad data
    // on disk.
    //
    // The gate is POSITIVE — `=== 'ready'`, not `!== 'error'`. Round 1 of
    // review: `!== 'error'` let 'loading' through, and 'loading' is equally
    // "we do not know". A merchant changing their kind in Account invalidates
    // the shared answer while this component stays mounted, and every tick in
    // that window went to the server under the fallback kind.
    //
    // Holding the write in a queue until the kind resolves was the tempting
    // alternative and is WRONG: the ids are per-kind too, so the step the
    // merchant ticked off the fallback list may not exist in the list they
    // actually have. Replaying it later just files a nonsense id under the
    // right kind. There is no honest deferred write here.
    const wouldRecord = !step.connectionGated && !step.complete;
    const refused = wouldRecord && kindStatus !== 'ready';

    if (wouldRecord && !refused) {
      // Optimistic tick + server-side merge; per-kind ids (steps differ per kind).
      setManualDone((m) => ({
        ...m,
        [businessType]: [...(m[businessType] ?? []), step.id],
      }));
      void api.userProfile
        .update({ setupStepDone: { kind: businessType, stepId: step.id } })
        .catch(() => undefined);
    }

    // Navigating still happens either way — taking the merchant to Integrations
    // is the useful half and does not depend on knowing the kind. But on a
    // REFUSAL the panel stays open, so the notice explaining why nothing ticked
    // is still on screen after the click that did not tick. Collapsing would
    // leave the merchant with an unexplained no-op.
    if (!refused) setExpanded(false);
    navigate(step.route);
  };

  return (
    <>
      {expanded && (
        <div
          className="fixed inset-0 z-[60] bg-black/30"
          onClick={() => setExpanded(false)}
          aria-hidden="true"
        />
      )}

      {expanded ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('setup.title')}
          className="fixed z-[61] bottom-20 lg:bottom-6 right-4 lg:right-6 w-[min(400px,calc(100vw-2rem))] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
        >
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100">
            <ProgressRing pct={doneCount / steps.length} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900">{t('setup.title')}</div>
              <div className="text-xs text-gray-400">
                {t('setup.subtitle', {
                  kind: BUSINESS_TYPE_LABELS[businessType],
                  done: doneCount,
                  total: steps.length,
                })}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label={t('setup.collapse')}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Keyed on "not ready", not on "error": the write gate below refuses
              on 'loading' too, and a refusal the merchant is not told about is
              the thing the brief forbids. The retry is offered only for
              'error', because a read that is still in flight has nothing to
              retry. This cannot flash on a normal load — the panel has to be
              opened by hand, and by then the read has long settled. */}
          {kindStatus !== 'ready' && (
            <div className="flex items-start gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-100">
              <span className="text-[11.5px] text-amber-800 leading-relaxed">
                {t('setup.kindUnknown')}
                {kindStatus === 'error' && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={retryKind}
                      className="font-medium underline underline-offset-2 hover:text-amber-900"
                    >
                      {t('setup.kindUnknownRetry')}
                    </button>
                  </>
                )}
              </span>
            </div>
          )}

          <div className="max-h-[55vh] overflow-y-auto divide-y divide-gray-50">
            {steps.map((step, i) => step.comingSoon ? (
              // What this step sets up is not live for a new customer yet
              // (config/featureAvailability.ts): no call to action, nothing
              // to click through to, just the Coming-soon label.
              <div key={step.id} data-testid={`setup-step-${step.id}`} className="w-full flex items-start gap-3 px-4 py-3 text-left">
                <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold shrink-0 bg-gray-100 text-gray-400">
                  {i + 1}
                </span>
                <span className="flex-1 min-w-0 block text-[13px] font-medium text-gray-500">
                  {t(step.titleKey)}
                  <ComingSoonBadge />
                </span>
              </div>
            ) : (
              <button
                key={step.id}
                type="button"
                onClick={() => takeStep(step)}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                  // Kit FirstRun: the FIRST incomplete step is visually "next" —
                  // without this, nothing tells the owner where to go.
                  !step.complete && steps.findIndex((x) => !x.complete && !x.comingSoon) === i ? 'bg-gray-50' : ''
                }`}
              >
                <span
                  className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold shrink-0 ${
                    step.complete ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {step.complete ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    className={`block text-[13px] font-medium ${
                      step.complete ? 'text-gray-400 line-through' : 'text-gray-900'
                    }`}
                  >
                    {t(step.titleKey)}
                  </span>
                  {!step.complete && step.bodyKey && (
                    <span className="block text-xs text-gray-500 mt-0.5 leading-relaxed">{t(step.bodyKey)}</span>
                  )}
                  {!step.complete && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 mt-1.5">
                      {t(step.ctaKey)}
                      <ChevronRight className="w-3 h-3" />
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>

          {/* Kit FirstRun footer: the reassurance line and the kind escape
              hatch — setup is a consequence of connecting, and the answer
              everything keys off stays changeable. */}
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-100">
            <span className="text-[11.5px] text-gray-400">{t('setup.footer.reassure')}</span>
            <button
              type="button"
              onClick={() => { setExpanded(false); navigate('/account?tab=profile'); }}
              className="text-[11.5px] font-medium text-gray-500 underline underline-offset-2 hover:text-gray-700"
            >
              {t('setup.footer.changeKind')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="fixed z-[61] bottom-20 lg:bottom-6 right-4 lg:right-6 flex items-center gap-2.5 pl-2 pr-3.5 py-2 bg-white border border-gray-200 rounded-full shadow-lg hover:shadow-xl transition-shadow"
        >
          <ProgressRing pct={doneCount / steps.length} />
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 tabular-nums">
            <ListChecks className="w-4 h-4 text-gray-400" />
            {t('setup.pill', { done: doneCount, total: steps.length })}
          </span>
        </button>
      )}
    </>
  );
}
